'use strict';

const BINARY_GATES = ['SWAP', 'XX', 'YY', 'ZZ'];

// Qubit.js requires Gate.js, so we can't just import it to do a type check.
// we should refactor to remove circular dependencies
const ensureQubit = (q) => {
    // FIXME Babel processing totally wrecks q.constructor.name == 'Qubit'
    // and that's essential for the dev site frontend. So... probably all of
    // this will be rewritten soon and thus I can write this painful arbitrary
    // __proto__ check.
    if (!(q && typeof q === 'object' && q.__proto__.isFree)) {
        throw new Error('not a qubit');
    }
};

/**
 * this class stores the gate type, target qubits, control qubits
 * and rotation necessary to perform a gate operation.  it is decoded
 * at execution time by the system class.
 */
class Gate {
    constructor(type, targets, controls, rotation) {
        this.type = type = type.toUpperCase();
        this.count = targets.length + (controls ? controls.length : 0);

        const isBinaryGate = BINARY_GATES.includes(type);

        if (isBinaryGate && targets.length !== 2) {
            throw new Error(`${type} gate requires exactly two target qubits`);
        }

        if (!isBinaryGate && targets.length !== 1) {
            throw new Error('gate requires only one target qubit');
        }

        let seenIndices = [];
        for (let i = 0; i < targets.length; ++i) {
            ensureQubit(targets[i]);
            if (seenIndices.indexOf(targets[i].index) !== -1) {
                throw new Error('duplicate qubit');
            }
            seenIndices.push(targets[i].index);
        }
        if (controls) {
            for (let i = 0; i < controls.length; ++i) {
                ensureQubit(controls[i]);
                if (seenIndices.indexOf(controls[i].index) !== -1) {
                    throw new Error('duplicate qubit');
                }
                seenIndices.push(controls[i].index);
            }
        }

        // resolve qubit to indices
        this.targets = this.resolve(targets);
        this.controls = this.resolve(controls || []);
        this.controlsOC = this.resolve(controls || [], true);

        if (Gate.acceptsAngle(type)) {
            this.rotation = typeof rotation === 'number' ? rotation : Math.PI;
            if (!Number.isFinite(this.rotation)) {
                throw new Error(`Non-finite rotation angle: ${this.rotation}`);
            }
        } else {
            if (rotation !== undefined) {
                throw new Error(
                    `rotation angle cannot be used with ${type} gate`,
                );
            }
        }
    }

    /** turn qubit or qubit array into an array of their indices */
    resolve(qubits, control) {
        const indices = [];
        qubits.forEach((qubit) => {
            ensureQubit(qubit);
            if (!qubit.circuit) {
                throw new Error('attempt to use freed qubit');
            }
            indices.push(control ? 1 / qubit.control >= 0 : qubit.index);
        });
        return indices;
    }

    /**
     * gate tensor offset into control tensor matrix
     */
    controlOffset() {
        return parseInt(
            this.controlsOC
                .map((oc) => {
                    return oc ? '1' : '0';
                })
                .join(''),
            2,
        );
    }

    static acceptsAngle(gateType) {
        return ['X', 'Y', 'Z', 'RX', 'RY', 'RZ', 'XX', 'YY', 'ZZ'].includes(
            gateType.toUpperCase(),
        );
    }
}

Gate.type = {
    NOT: 'NOT',
    H: 'H',
    X: 'X',
    Y: 'Y',
    Z: 'Z',
    RX: 'RX',
    RY: 'RY',
    RZ: 'RZ',
    SWAP: 'SWAP',
    S: 'S',
    SI: 'SI',
    T: 'T',
    TI: 'TI',
    V: 'V',
    VI: 'VI',
    R2: 'R2',
    R4: 'R4',
    R8: 'R8',
    XX: 'XX',
    YY: 'YY',
    ZZ: 'ZZ',
};

module.exports = Gate;
